import { useCreate, useGetIdentity, useNotify, useTranslate } from "ra-core";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import {
  CONTACT_TYPE_CHOICES,
  CONTACT_TYPE_LABEL_KEYS,
  contactCardPath,
  contactTypeLabel,
  isCreatableContactType,
  type CreatableContactType,
} from "../palmtree/constants";
import { sectionLabelClass } from "../palmtree/shell";
import type { Contact } from "../types";
import { cleanupContactForCreate } from "./contactModel";

export const ContactCreateDialog = ({
  open,
  onOpenChange,
  defaultType,
  onCloseAutoFocus,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultType: CreatableContactType;
  onCloseAutoFocus?: (event: Event) => void;
}) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const [create, { isPending }] = useCreate();
  const [type, setType] = useState<CreatableContactType>(defaultType);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [nameError, setNameError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType(defaultType);
    setName("");
    setEmail("");
    setPhone("");
    setNameError(false);
  }, [open, defaultType]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(true);
      return;
    }
    const trimmedPhone = phone.trim();
    const phone_jsonb: Contact["phone_jsonb"] = trimmedPhone
      ? [{ number: trimmedPhone, type: "Work" }]
      : [];
    try {
      const record = await create(
        "contacts",
        {
          data: {
            ...cleanupContactForCreate({
              first_name: trimmed,
              last_name: "",
              type,
              sales_id: identity?.id,
              status: "Warm",
              preferred_channel: "Telefoon",
              gender: "unknown",
              email_jsonb: email.trim()
                ? [{ email: email.trim(), type: "Work" }]
                : [],
              phone_jsonb: [],
            } as unknown as Contact),
            phone_jsonb,
          },
        },
        { returnPromise: true },
      );
      notify("ra.notification.created", { type: "info" });
      onOpenChange(false);
      if (record?.id != null) navigate(contactCardPath(record.id));
    } catch (error) {
      notify(
        error instanceof Error && error.message
          ? error.message
          : "ra.notification.http_error",
        { type: "error" },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>{translate("palmtree.contact.new")}</DialogTitle>
          <DialogDescription>
            {translate("palmtree.contact.type_required", {
              type: contactTypeLabel(defaultType),
            })}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="flex flex-col gap-2">
            <span className={sectionLabelClass}>
              {translate("palmtree.contact.field_type")}{" "}
              <span className="text-warn">*</span>
            </span>
            <div
              className="grid grid-cols-2 gap-2"
              role="radiogroup"
              aria-label={translate("palmtree.contact.field_type")}
            >
              {CONTACT_TYPE_CHOICES.map((id) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={type === id}
                  onClick={() => setType(id)}
                  className={cn(
                    "rounded-md border px-3 py-2 text-sm",
                    type === id
                      ? "border-primary bg-primary/10 font-semibold text-primary"
                      : "border-border bg-card",
                  )}
                >
                  {translate(CONTACT_TYPE_LABEL_KEYS[id])}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="nieuw-contact-naam" className={sectionLabelClass}>
              {translate("palmtree.contact.name")}{" "}
              <span className="text-warn">*</span>
            </label>
            <Input
              id="nieuw-contact-naam"
              aria-label={translate("palmtree.contact.name")}
              value={name}
              aria-invalid={nameError}
              placeholder={translate("palmtree.contact.name_placeholder")}
              onChange={(event) => {
                setName(event.target.value);
                if (event.target.value.trim()) setNameError(false);
              }}
            />
            {nameError ? (
              <p className="text-sm text-warn">
                {translate("palmtree.contact.required")}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="nieuw-contact-email" className={sectionLabelClass}>
              {translate("palmtree.contact.email")}
            </label>
            <Input
              id="nieuw-contact-email"
              aria-label={translate("palmtree.contact.email")}
              type="email"
              value={email}
              placeholder={translate("palmtree.contact.optional")}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label
              htmlFor="nieuw-contact-telefoon"
              className={sectionLabelClass}
            >
              {translate("palmtree.contact.phone")}
            </label>
            <Input
              id="nieuw-contact-telefoon"
              aria-label={translate("palmtree.contact.phone")}
              type="tel"
              value={phone}
              placeholder={translate("palmtree.contact.optional")}
              onChange={(event) => setPhone(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {translate("palmtree.contact.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {translate("palmtree.contact.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/** Resource create route redirects here; stories render the dialog directly. */
export const ContactCreate = () => {
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get("type");
  const contactType = isCreatableContactType(requestedType)
    ? requestedType
    : "person";
  return (
    <ContactCreateDialog
      open
      defaultType={contactType}
      onOpenChange={() => undefined}
    />
  );
};
